import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ContestViewContest } from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { SearchItem } from '@freelancer/ui/search';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import {
  EntryFilterOption,
  EntrySortOption,
} from '../contest-view/contest-view.component';

@Component({
  template: `
    <fl-bit flTrackingSection="ContestViewPage.AdvancedFiltersModal">
      <fl-heading
        i18n="Advanced filters header"
        [size]="TextSize.MID"
        [headingType]="HeadingType.H1"
        [flMarginBottom]="Margin.MID"
      >
        Advanced Filters
      </fl-heading>
      <fl-text
        i18n="Search entries text"
        [weight]="FontWeight.BOLD"
        [flMarginBottom]="Margin.XXSMALL"
      >
        Search
      </fl-text>
      <fl-bit [flMarginBottom]="Margin.MID">
        <app-entry-search
          [contest$]="contest$"
          [adjustableMaxResultsPerContext$]="entrySearchLimitPerContext$"
          (onSearchFilterSelect)="handleSelectedSearchFilter($event)"
          (onSearchFilterRemove)="handleSearchFilterRemove()"
        ></app-entry-search>
      </fl-bit>
      <fl-text
        i18n="View entries text"
        [weight]="FontWeight.BOLD"
        [flMarginBottom]="Margin.XXSMALL"
      >
        View Entries
      </fl-text>
      <fl-select
        flTrackingLabel="SelectFilterOption"
        [control]="entryFilterControl"
        [options]="entryFilterOptions"
        [flMarginBottom]="Margin.MID"
      ></fl-select>
      <fl-text
        i18n="Sort by text"
        [weight]="FontWeight.BOLD"
        [flMarginBottom]="Margin.XXSMALL"
      >
        Sort By
      </fl-text>
      <fl-select
        flTrackingLabel="SelectSortOption"
        [control]="entrySortControl"
        [options]="entrySortOptions"
      ></fl-select>
      <fl-container
        class="EntryAdvancedFilters-cta"
        [flSticky]="true"
        [flStickyBehaviour]="StickyBehaviour.ALWAYS"
        [flStickyPosition]="StickyPosition.BOTTOM"
      >
        <fl-link
          i18n="Reset filters link"
          flTrackingLabel="ClickResetFilters"
          [flMarginBottom]="Margin.LARGE"
          (click)="handleResetFiltersClick()"
        >
          Reset Filters
        </fl-link>
        <fl-button
          i18n="Apply filters button"
          flTrackingLabel="ClickApplyFilters"
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.SMALL"
          [display]="'block'"
          (click)="handleApplyFiltersClick()"
        >
          Apply Filters
        </fl-button>
      </fl-container>
    </fl-bit>
  `,
  styleUrls: ['./entry-advanced-filters-modal.component.scss'],
})
export class EntryAdvancedFiltersModalComponent implements OnInit, OnDestroy {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontWeight = FontWeight;
  TextSize = TextSize;
  HeadingType = HeadingType;
  Margin = Margin;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  @Input() currentEntryFilterOption$: Rx.Observable<EntryFilterOption>;
  @Input() currentEntrySortOption$: Rx.Observable<EntrySortOption>;
  @Input() contest$: Rx.Observable<ContestViewContest>;

  readonly ENTRY_SEARCH_LIMIT_PER_CONTEXT = 3;
  entrySearchLimitPerContext$ = Rx.of(this.ENTRY_SEARCH_LIMIT_PER_CONTEXT);

  currentEntryFilterOptionSubscription?: Rx.Subscription;
  currentEntrySortOptionSubscription?: Rx.Subscription;

  entryFilterOptions = Object.values(EntryFilterOption);
  entryFilterControl = new FormControl();

  entrySortOptions = Object.values(EntrySortOption);
  entrySortControl = new FormControl();

  constructor(private modalRef: ModalRef<EntryAdvancedFiltersModalComponent>) {}

  ngOnInit() {
    this.currentEntryFilterOptionSubscription = this.currentEntryFilterOption$.subscribe(
      filterOption => this.entryFilterControl.setValue(filterOption),
    );

    this.currentEntrySortOptionSubscription = this.currentEntrySortOption$.subscribe(
      sortOption => this.entrySortControl.setValue(sortOption),
    );
  }

  ngOnDestroy() {
    if (this.currentEntryFilterOptionSubscription) {
      this.currentEntryFilterOptionSubscription.unsubscribe();
    }

    if (this.currentEntrySortOptionSubscription) {
      this.currentEntrySortOptionSubscription.unsubscribe();
    }
  }

  handleResetFiltersClick() {
    this.entryFilterControl.setValue(EntryFilterOption.ALL_ENTRIES);
    this.entrySortControl.setValue(EntrySortOption.DEFAULT);
  }

  handleApplyFiltersClick() {
    this.modalRef.close({
      filter: this.entryFilterControl.value,
      sort: this.entrySortControl.value,
    });
  }

  handleSelectedSearchFilter(searchFilter: SearchItem) {
    this.modalRef.close({
      searchFilter,
    });
  }

  handleSearchFilterRemove() {
    this.modalRef.close({ searchFilter: {} });
  }
}
